/**
 * Page /nouveau-format — Manifeste éditorial Code 2 Roues 2026.
 *
 * Annonce publique du pivot : abandon du format QCM (concept proche
 * Code Rousseau) au profit d'un format "magazine" éditorial structuré
 * par les 8 thématiques ETM officielles.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, BookOpenCheck, Layers, Compass, Heart, Sparkles, ArrowRight, CheckCircle2, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const THEMES_ETM = [
  { code: 'C', label: 'Circulation', desc: 'Intersections, priorités, dépassements, voies spécifiques.' },
  { code: 'L', label: 'Le conducteur', desc: 'Vigilance, fatigue, alcool, vitesse, attitude responsable.' },
  { code: 'R', label: 'La route', desc: 'Signalisation, marquage au sol, types de chaussées.' },
  { code: 'A', label: 'Les autres usagers', desc: 'Piétons, cyclistes, poids lourds, partage de la route.' },
  { code: 'D', label: 'Réglementation', desc: 'Code, sanctions, permis à points, assurance.' },
  { code: 'S', label: 'Sécurité du véhicule', desc: 'Vérifications, équipement, entretien.' },
  { code: 'E', label: 'Environnement', desc: 'Pollution, éco-conduite, mécanique propre.' },
  { code: 'P', label: 'Premiers secours', desc: 'PAS, alerte, gestes à connaître en cas d’accident.' },
];

const PILIERS = [
  {
    icon: Newspaper,
    title: 'Format magazine, pas QCM',
    body: 'Chaque thématique se lit comme un long-read éditorial : photos, citations, encadrés pédagogiques. Vous comprenez en profondeur, vous ne récitez pas.',
  },
  {
    icon: Layers,
    title: '8 thématiques ETM officielles',
    body: 'On reprend la structure du référentiel d’Épreuve Théorique Moto (ETM) — mais on en fait un véritable contenu de fond, pas une banque de questions.',
  },
  {
    icon: BookOpenCheck,
    title: 'Pensé par des enseignants ECSR',
    body: 'Chaque article est rédigé et illustré par des moniteurs de la conduite et de la sécurité routière. Pas de générateur automatique : du vécu, de la pratique, de la pédagogie.',
  },
  {
    icon: Heart,
    title: 'Sans pression du score',
    body: 'Fini la course aux 35/40. On vous apprend à raisonner, à anticiper, à décider. La compréhension prime sur la mémorisation.',
  },
];

export default function NouveauFormat() {
  useEffect(() => {
    document.title = 'Nouveau format magazine · Code 2 Roues';
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14" data-testid="nouveau-format-page">
      {/* Hero */}
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Manifeste · 2026</span>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-5"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          On a changé de cap.<br />
          <span className="text-[hsl(var(--primary))]">Plus de QCM,</span>{' '}plus de magazine.
        </h1>
        <p className="text-lg sm:text-xl text-foreground/80 leading-relaxed max-w-3xl">
          Code 2 Roues abandonne le format <em>banque de questions à rallonge</em> au profit d’un
          <strong> magazine éditorial</strong> structuré par les 8 thématiques ETM. Notre conviction&nbsp;:
          on n’apprend pas la conduite en cochant des cases. On l’apprend en comprenant
          le pourquoi.
        </p>
      </div>

      {/* Citation pilier */}
      <div className="relative my-12 p-6 sm:p-8 rounded-2xl bg-[hsl(var(--primary))]/5 border-l-4 border-[hsl(var(--primary))]">
        <Quote className="w-8 h-8 text-[hsl(var(--primary))] opacity-30 absolute top-4 left-4" />
        <blockquote className="text-lg sm:text-xl font-semibold italic leading-relaxed pl-8">
          « Un élève qui comprend pourquoi il s’arrête au stop réussira toujours mieux qu’un élève qui a mémorisé que "stop = s’arrêter". Notre métier, c’est de faire naître la compréhension, pas la récitation. »
        </blockquote>
        <cite className="block mt-4 pl-8 text-sm not-italic text-muted-foreground">
          — Bruno Llorens, Enseignant ECSR · fondateur de Code 2 Roues
        </cite>
      </div>

      {/* Les 4 piliers */}
      <h2 className="text-2xl sm:text-3xl font-black mb-6 mt-16" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Ce qui change concrètement
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        {PILIERS.map((p) => (
          <Card key={p.title} className="overflow-hidden">
            <CardContent className="p-5">
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center mb-3">
                <p.icon className="w-5 h-5" />
              </div>
              <h3 className="font-black text-base mb-1.5">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tableau Avant/Après */}
      <h2 className="text-2xl sm:text-3xl font-black mb-6 mt-16" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Avant → maintenant
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-rose-600 mb-3">L’ancien format</div>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2"><span className="text-rose-600 font-black">×</span>Banques de questions à réciter (style Code Rousseau)</li>
              <li className="flex gap-2"><span className="text-rose-600 font-black">×</span>Sessions chronométrées, pression du score</li>
              <li className="flex gap-2"><span className="text-rose-600 font-black">×</span>Mode « noooté » vs « libre » — confus</li>
              <li className="flex gap-2"><span className="text-rose-600 font-black">×</span>Explications courtes, peu de contexte</li>
            </ul>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-3">Le nouveau format</div>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />Articles éditoriaux par thématique ETM</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />Lecture libre, sans chrono, comme un magazine</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />Encadrés pédago « À retenir » en fin de section</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />Photos, citations, mises en scène réelles</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Les 8 thématiques ETM */}
      <h2 className="text-2xl sm:text-3xl font-black mb-2 mt-16" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Les 8 thématiques ETM
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Chaque thématique du référentiel officiel devient un véritable <strong>dossier magazine</strong> avec un article-pilier et plusieurs articles satellites.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
        {THEMES_ETM.map((t) => (
          <Card key={t.code} className="hover:border-[hsl(var(--primary))]/40 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">Thématique</span>
                <span className="text-[10px] font-black text-muted-foreground">{t.code}</span>
              </div>
              <h3 className="font-black text-sm mb-1.5">{t.label}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQ courte */}
      <h2 className="text-2xl sm:text-3xl font-black mb-6 mt-16" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Questions légitimes
      </h2>
      <div className="space-y-4 mb-12">
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h3 className="font-black text-sm mb-1.5">« J’avais commencé le QCM — mes progrès sont perdus ? »</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Non. Votre compte, votre profil et votre historique restent intacts. Simplement, le parcours QCM est progressivement remplacé par les dossiers magazine. Vous gagnez en compréhension, vous ne perdez rien.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h3 className="font-black text-sm mb-1.5">« Et pour s’entraîner à l’examen ETM officiel ? »</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Code 2 Roues n’a jamais été un simulateur d’examen officiel. C’est un outil de <strong>fond</strong>&nbsp;: vous travaillez votre culture moto, vos réflexes de conduite, votre raisonnement. Les simulations d’épreuve restent du ressort de votre auto-école.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h3 className="font-black text-sm mb-1.5">« Est-ce que tout est gratuit ? »</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Oui. L’accès aux dossiers magazine reste gratuit pour tous. C’est notre engagement initial et il ne change pas.
          </p>
        </div>
      </div>

      {/* CTA final */}
      <div className="text-center py-12 px-6 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))]/10 via-[hsl(var(--primary))]/5 to-transparent border border-[hsl(var(--primary))]/20">
        <Compass className="w-10 h-10 text-[hsl(var(--primary))] mx-auto mb-4" />
        <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Prêt à explorer ?
        </h2>
        <p className="text-base text-muted-foreground mb-6 max-w-xl mx-auto">
          Le nouveau format est déjà disponible. Commencez par la thématique qui vous intéresse, lisez à votre rythme, prenez du plaisir.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild size="lg" data-testid="nouveau-format-cta-magazine">
            <Link to="/magazine/bien-choisir-casque-moto">Découvrir le magazine <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" data-testid="nouveau-format-cta-home">
            <Link to="/">Retour à l’accueil</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
